import { Box, Chip, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import { useSessionStorage } from 'react-use';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import RecordTimeline from '@components/RecordTimeline/RecordTimeline';
import TimelineLoader from '@components/TimelineLoader';

import { ScanResultProvider } from '@contexts/index';

import { useGetSharedResource } from '@hooks/api/shared_resource';
import { transformResource } from '@hooks/api/shared_resource';
import { getVitalSignResults } from '@hooks/api/vital-sign';
import { useConversion } from '@hooks/useConversion';
import { useIsMobile } from '@hooks/useIsMobile';
import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSnackbar } from '@hooks/useSnackbar';

import type { AllergyRecord } from '@models/allergy';
import type { AppointmentRecord } from '@models/appointment';
import type { SharedAssessmentResult as SharedAssessmentResultRecord } from '@models/assessment/shared';
import type { DiagnosticReportRecord } from '@models/diagnostic-report';
import type { EncounterRecord } from '@models/encounter';
import type { FamilyHistoryRecord } from '@models/family-history';
import type { GeneticInformationObservationRecord } from '@models/genetic-information';
import type { ImmunizationRecord } from '@models/immunization';
import type { InsurancePlan } from '@models/insurance';
import type { LabResultObservationRecord } from '@models/lab-result';
import type { LegalDirectiveRecord } from '@models/legal-directive';
import type { MedicalConditionRecord } from '@models/medical-condition';
import type { MedicationStatementRecord } from '@models/medication';
import type { ObservationRecord } from '@models/observation';
import type { PatientRecord } from '@models/patient';
import type { HealthSummaryData, TimelineData } from '@models/patient-record';
import type { ProcedureRecord } from '@models/procedure';
import type { RelatedPersonRecord } from '@models/related-person';
import type { SharedScanResult } from '@models/scan';
import type { VitalSignObservationRecord } from '@models/vital-sign';

import { formatDate, formatFhirCode } from '@utils/format';

import SharedAssessmentResult from '@views/Assessments/Shared/SharedAssessmentResult';
import HealthSummaryContent from '@views/HealthSummary/Main/Main';
import AllergyContent from '@views/Records/Allergies/Content';
import AppointmentContent from '@views/Records/Appointments/Content';
import ErContent from '@views/Records/EmergencyVisits/Content';
import FamilyHistoryContent from '@views/Records/FamilyHistory/Content';
import GeneticInfoList from '@views/Records/GeneticInformation/Main';
import ObservationsList from '@views/Records/HealthTracker/Sharing';
import HospitalizationContent from '@views/Records/Hospitalizations/Content';
import ImagingTestContent from '@views/Records/ImagingTests/Content';
import ImmunizationList from '@views/Records/Immunizations/ImmunizationList';
import InsuranceContent from '@views/Records/Insurance/Content';
import LabResultList from '@views/Records/LabResults/Main';
import ConditionContent from '@views/Records/MedicalConditions/Content';
import MedicationContent from '@views/Records/Medications/Content';
import SharedMedicationList from '@views/Records/Medications/SharedMedicationList';
import ScanMain from '@views/Records/Scan/Content';
import CoughScanContent from '@views/Records/Scan/Drilldown/Content';
import ProcedureContent from '@views/Records/Surgeries/Content';

import { type ScanResultModel, ShareType } from 'generated/types';

import SharedResourceExpiryErrorFallback from './ExpiryErrorFallback';

const messages = defineMessages({
  expirySingle: {
    id: 'sharedResource.access.expiry.single',
    defaultMessage: 'You can access this record until {date} at {time}.',
  },
  expiryCollection: {
    id: 'sharedResource.access.expiry.collection',
    defaultMessage: 'You can access these records until {date} at {time}.',
  },
  error: {
    id: 'errorMessage.pagination',
    defaultMessage:
      'An error occurred while trying to retrieve more records. Please try again later.',
  },
});

interface SharedResourceProps {
  /**
   * The share id
   */
  share_id: string;
  /**
   * Called when the user name is set
   */
  onSetUserName?: (name: string) => void;
  /**
   * Called when there is an error in fetching the resource
   */
  onError?: () => void;
}

function SharedResource({
  share_id,
  onSetUserName,
  onError,
}: SharedResourceProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { handleError } = useResponseHandler();
  const isMobile = useIsMobile();
  const {
    state: { conversion },
  } = useConversion();
  const [token] = useSessionStorage('share_token');

  const {
    data: {
      cursor,
      expiry,
      has_more,
      patient_id,
      resource,
      share_title,
      share_type,
      sharer_name,
    },
    loading,
    error,
    fetchMore,
  } = useGetSharedResource(share_id);

  const [paginationState, setPaginationState] = useState({
    hasMoreData: has_more,
    pageCursor: cursor,
    records: resource,
  });

  useEffect(() => {
    setPaginationState(() => ({
      hasMoreData: has_more,
      pageCursor: cursor,
      records: resource,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  const resources = useMemo(
    () => paginationState.records ?? [],
    [paginationState.records]
  );

  const fetchMoreData = async () => {
    if (paginationState.hasMoreData === false || !paginationState.pageCursor) {
      return;
    }
    try {
      const { data } = await fetchMore({
        variables: {
          input: {
            share_link: share_id,
            cursor: paginationState.pageCursor,
          },
        },
        context: {
          headers: {
            authorization: token,
          },
        },
      });
      const nextBatch = data.getSharedResource;
      const newTransformedRecords = transformResource(nextBatch.resource);

      setPaginationState(() => {
        return {
          hasMoreData: nextBatch.has_more,
          pageCursor: nextBatch.cursor,
          records: [
            ...(paginationState.records ?? []),
            ...(newTransformedRecords ?? []),
          ],
        };
      });
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  };

  const baseProps = {
    isSharing: true,
    loading,
    patientId: patient_id,
  };

  const isCollection = share_type && share_type !== ShareType.SingleResource;

  React.useEffect(() => {
    if (sharer_name) {
      onSetUserName?.(sharer_name);
    }
  }, [onSetUserName, sharer_name]);

  React.useEffect(() => {
    if (
      error &&
      error.graphQLErrors.some((error) => {
        const { extensions, code } = error as any;
        // If unauthorized, emit onError event and allow user to retry
        return (extensions?.code ?? code) === '401';
      })
    ) {
      onError?.();
    } else if (error) {
      handleError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const displayResource = (isLoading?: boolean) => {
    if (isCollection) {
      if (
        share_type.startsWith('VitalSigns') ||
        (share_type.startsWith('Body') && share_type !== ShareType.BodyScans) ||
        share_type.startsWith('DailyStats') ||
        share_type.startsWith('Sleep')
      ) {
        const [, code] = share_title?.split(':') ?? '';
        const observations =
          resources as unknown as VitalSignObservationRecord[];
        const results = getVitalSignResults(code, observations, conversion);
        return <ObservationsList {...baseProps} data={results} code={code} />;
      }
      switch (share_type) {
        case ShareType.FaceScans:
        case ShareType.BodyScans:
        case ShareType.CoughScans:
        case ShareType.SkinScans:
          // eslint-disable-next-line no-case-declarations
          const scans = resources as unknown as SharedScanResult[];
          // eslint-disable-next-line no-case-declarations
          let scan_type;
          switch (share_type) {
            case ShareType.FaceScans:
              scan_type = 'face-scan-history';
              break;
            case ShareType.BodyScans:
              scan_type = 'body-scan-history';
              break;
            case ShareType.CoughScans:
              scan_type = 'cough-scan-history';
              break;
            case ShareType.SkinScans:
              scan_type = 'skin-scan-history';
              break;
          }
          return (
            <ScanResultProvider isSharing scanType={scan_type}>
              <ScanMain
                {...baseProps}
                data={scans}
                hasMore={paginationState.hasMoreData ?? undefined}
                loading={isLoading ?? loading}
                onEnter={fetchMoreData}
              />
            </ScanResultProvider>
          );
        case ShareType.Immunizations:
          return (
            <ImmunizationList
              {...baseProps}
              immunizations={resources as unknown as ImmunizationRecord[]}
            />
          );
        case ShareType.GeneticInformation:
          return (
            <GeneticInfoList
              data={
                resources as unknown as GeneticInformationObservationRecord[]
              }
              loading={loading}
            />
          );
        case ShareType.HealthSummary:
          // eslint-disable-next-line no-case-declarations
          const summaryRecords = resources.reduce<HealthSummaryData>(
            (acc, resource) => {
              switch (resource?.resourceType) {
                case 'AllergyIntolerance':
                  acc.allergy.push(resource as AllergyRecord);
                  break;
                case 'Condition':
                  acc.condition.push(resource as MedicalConditionRecord);
                  break;
                case 'Immunization':
                  acc.immunization.push(resource as ImmunizationRecord);
                  break;
                case 'MedicationStatement':
                  acc.medication_statement.push(
                    resource as MedicationStatementRecord
                  );
                  break;
                case 'Procedure':
                  acc.procedure.push(resource as ProcedureRecord);
                  break;
                case 'Consent':
                  acc.legal_directives.push(resource as LegalDirectiveRecord);
                  break;
                case 'RelatedPerson':
                  acc.related_persons.push(resource as RelatedPersonRecord);
                  break;
                case 'Coverage':
                  acc.coverage.push(resource as InsurancePlan);
                  break;
                case 'Patient':
                  acc.patient = resource as PatientRecord;
                  break;
                case 'Observation':
                  // eslint-disable-next-line no-case-declarations
                  const code = (resource as ObservationRecord).code;
                  switch (code) {
                    case 'blood-type':
                      acc.blood_type = resource as LabResultObservationRecord;
                      break;
                    case 'body-weight':
                    case 'weight':
                      acc.weight_body_observation =
                        resource as VitalSignObservationRecord;
                      break;
                    case 'body-height':
                      acc.height_body_observation =
                        resource as VitalSignObservationRecord;
                      break;
                  }
                  break;
                default:
                  return acc;
              }
              return acc;
            },
            {
              allergy: [],
              condition: [],
              immunization: [],
              medication_statement: [],
              procedure: [],
              legal_directives: [],
              related_persons: [],
              coverage: [],
              patient: null,
              blood_type: null,
              weight_body_observation: null,
              height_body_observation: null,
            }
          );

          return (
            <Box sx={{ py: 2 }}>
              <HealthSummaryContent {...baseProps} data={summaryRecords} />
            </Box>
          );
        case ShareType.DigitalHealthAssessment:
          return (
            <SharedAssessmentResult
              loading={loading}
              record={
                resources.at(0) as unknown as SharedAssessmentResultRecord
              }
            />
          );
        case ShareType.LabResults:
          return (
            <LabResultList
              data={resources as unknown as DiagnosticReportRecord[]}
              loading={loading}
            />
          );
        case ShareType.Medications:
          return (
            <SharedMedicationList
              loading={loading}
              medications={resources as unknown as MedicationStatementRecord[]}
            />
          );
        case ShareType.Timeline:
          // eslint-disable-next-line no-case-declarations
          const timelineRecords = resources.reduce<TimelineData>(
            (acc, resource) => {
              switch (resource?.resourceType) {
                case 'Appointment':
                  acc.appointment.push(resource as AppointmentRecord);
                  break;
                case 'DiagnosticReport':
                  acc.diagnostic_report.push(
                    resource as DiagnosticReportRecord
                  );
                  break;
                case 'Encounter':
                  // eslint-disable-next-line no-case-declarations
                  const type = (resource as EncounterRecord).type;
                  switch (type) {
                    case 'emergency':
                      acc.emergency_visits.push(resource as EncounterRecord);
                      break;
                    case 'hospitalization':
                      acc.hospitalizations.push(resource as EncounterRecord);
                      break;
                  }
                  break;
                case 'Immunization':
                  acc.immunization.push(resource as ImmunizationRecord);
                  break;
                case 'Procedure':
                  acc.procedure.push(resource as ProcedureRecord);
                  break;
                default:
                  return acc;
              }
              return acc;
            },
            {
              appointment: [],
              diagnostic_report: [],
              emergency_visits: [],
              hospitalizations: [],
              immunization: [],
              procedure: [],
            }
          );
          return (
            <RecordTimeline
              data={timelineRecords}
              loading={loading}
              order="desc"
            />
          );

        default:
          return null;
      }
    }
    const resource = resources.at(0);
    switch (resource?.resourceType) {
      case 'AllergyIntolerance':
        return (
          <AllergyContent {...baseProps} data={resource as AllergyRecord} />
        );
      case 'Appointment':
        return (
          <AppointmentContent
            {...baseProps}
            data={resource as AppointmentRecord}
          />
        );
      case 'Condition':
        return (
          <ConditionContent
            {...baseProps}
            data={resource as MedicalConditionRecord}
          />
        );
      case 'Coverage':
        return (
          <InsuranceContent {...baseProps} data={resource as InsurancePlan} />
        );
      case 'Encounter':
        // eslint-disable-next-line no-case-declarations
        const record = resource as EncounterRecord;
        if (record.type === 'emergency') {
          return <ErContent {...baseProps} data={record} />;
        } else if (record.type === 'hospitalization') {
          return <HospitalizationContent {...baseProps} data={record} />;
        } else {
          return null;
        }
      case 'FamilyMemberHistory':
        return (
          <FamilyHistoryContent
            {...baseProps}
            data={resource as FamilyHistoryRecord}
          />
        );
      case 'MedicationStatement':
        return (
          <MedicationContent
            {...baseProps}
            isPatient={false}
            data={resource as MedicationStatementRecord}
          />
        );
      case 'Procedure':
        return (
          <ProcedureContent {...baseProps} data={resource as ProcedureRecord} />
        );
      case 'DiagnosticReport':
        return (
          <ImagingTestContent
            {...baseProps}
            canViewImgs
            data={resource as DiagnosticReportRecord}
          />
        );
      case 'SharedScanResult':
        return (
          <ScanResultProvider isSharing scanType="cough-scan-history">
            <CoughScanContent
              data={resource as ScanResultModel}
              loading={loading}
            />
          </ScanResultProvider>
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    if (
      share_type?.startsWith('VitalSigns') ||
      (share_type?.startsWith('Body') && share_type !== ShareType.BodyScans) ||
      share_type?.startsWith('DailyStats') ||
      share_type?.startsWith('Sleep')
    ) {
      const [category, code] = share_title?.split(':') ?? '';
      return `${formatFhirCode(category)}: ${formatFhirCode(code)}`;
    } else {
      return share_title;
    }
  };

  const chip = (
    <Chip
      label={formatMessage(
        isCollection ? messages.expiryCollection : messages.expirySingle,
        {
          date: formatDate({ date: expiry }),
          time: formatDate({ date: expiry, formatter: 'time' }),
        }
      )}
      sx={({ palette: { primary, background } }) => ({
        backgroundColor: `${background.paper}33`,
        color: primary.contrastText,
        textTransform: 'none',
        fontSize: 14,
        width: 'fit-content',
        padding: 1,
        height: 'auto',
        '& .MuiChip-label': {
          display: 'block',
          whiteSpace: 'normal',
        },
      })}
    />
  );

  {
    /** If the shared resource set is paginated */
  }
  if (paginationState.pageCursor) {
    const title = getTitle();
    const isTimelineDisplay =
      share_type === ShareType.FaceScans ||
      share_type === ShareType.BodyScans ||
      share_type === ShareType.CoughScans ||
      share_type === ShareType.SkinScans ||
      share_type === ShareType.Timeline;
    return (
      <>
        <ContentLoader
          lineCount={1}
          loading={!(expiry || sharer_name || share_type)}
        >
          {chip}
        </ContentLoader>
        <Box>
          <ContentLoader loading={!title}>
            <Typography color="grey.300" variant="h5">
              {title}
            </Typography>
          </ContentLoader>

          {(resource ?? []).length > 0 && loading && displayResource(false)}
          {isTimelineDisplay ? (
            <TimelineLoader
              count={!paginationState.pageCursor ? (isMobile ? 4 : 8) : 2}
              loading={loading}
            >
              {displayResource()}
            </TimelineLoader>
          ) : (
            <ContentLoader loading={loading}>{displayResource()}</ContentLoader>
          )}
        </Box>
      </>
    );
  }

  return (
    <ContentLoader loading={loading}>
      <Stack spacing={3}>
        {chip}

        <Box>
          <Typography color="grey.300" variant="h5">
            {getTitle()}
          </Typography>
          {displayResource()}
        </Box>
      </Stack>
    </ContentLoader>
  );
}

export default withErrorBoundary(SharedResource, {
  FallbackComponent: (props) => {
    if (props.error.code === '404') {
      return <SharedResourceExpiryErrorFallback {...props} />;
    }
    return <ErrorBoundaryFallback {...props} />;
  },
});
